import React from 'react';
import Form from 'react-bootstrap/Form';
import Datetime from 'react-datetime';
import moment from 'moment';
import Modal from 'react-bootstrap/Modal';
import shortid from 'shortid';
import ReactTooltip from 'react-tooltip';
import IconButton from './IconButton';
import InputGroup from 'react-bootstrap/InputGroup';
import './DatePicker.css';

export interface DatePickerProps {
    /** Shows time selector if set to true */
    showTime?: boolean;
    /** Change handler that recieves a moment object */
    onChange: Function;
    /** Type of toggle or display when not selecting */
    type?: "button" | "input" | "effective-date-icon" | "custom";
    CustomInput?: any;
    /** Value to display in datepicker. Can be any moment parsable date format */
    value: any;
    /** Name for the component */
    name?: string;
    /** Props for the button recieved as an object */
    buttonProps?: any;
    /** Props for the input recieved as an object */
    inputProps?: any;
    hasSelected?: boolean;
    formatBack?: string | null;
    /** Custom confirm handler */
    onConfirm?: boolean;
    /** Classname to be appended to input wrapper */
    inputWrapperClassName?: string;
    /** Shows 15 minute interval selectors */
    showAdditionalTimeControls?: boolean;
    /** When true the confirm button must be clicked to fire off change handler */
    useCloseCallback: boolean;
    appendClassName?: string;
    /** Shows date controls if set to true */
    showDate?: boolean;
    /** Disabled input and date selector modal from opening */
    disabled?: boolean;
    /** Display and manipulate values in UTC time */
    isUTC?: boolean;
    /** Custom id for input */
    inputId?: string;
    /** Custom error message for when date is not valid */
    errorMessage?: string;
    isValidDate?: Function;
}

const isFirefox = navigator.userAgent.toLowerCase().indexOf('firefox') > -1;

const ttId = shortid.generate();

const DatePicker: React.FC<DatePickerProps> = (props: DatePickerProps) => {
    const { 
        type, 
        value, 
        CustomInput, 
        onChange, 
        showTime, 
        hasSelected, 
        formatBack, 
        onConfirm, 
        appendClassName, 
        showDate, 
        isUTC, 
        inputId,
        errorMessage
    } = props;

    // const inputTimeFormat = (showTime && !showDate)
    //     ? moment.HTML5_FMT.TIME
    //     : showTime
    //         ? moment.HTML5_FMT.DATETIME_LOCAL
    //         : moment.HTML5_FMT.DATE;
    const inputTimeFormat = (showTime && showDate) 
        ? moment.HTML5_FMT.DATETIME_LOCAL 
        : (showTime && !showDate) 
            ? moment.HTML5_FMT.TIME 
            : moment.HTML5_FMT.DATE;

    const [open, setOpen] = React.useState(false);
    const [inputValue, setInputValue] = React.useState(isUTC ? moment.utc(value).format(inputTimeFormat) : moment(value).format(inputTimeFormat));
    const [datePickerValue, setDatePickerValue] = React.useState(moment(value));

    const handleClick = (e: React.SyntheticEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setOpen(true);
    }

    const handleChange = (val: any) => {
        //console.log("Value : ", val);
        if (onConfirm) {
            setDatePickerValue(val);
            return;
        }
        if (formatBack) {
            val = val.format(formatBack);
        }
        if (props.name && !onConfirm) {
            onChange && onChange(val, props.name);
        } else if (!onConfirm) {
            onChange && onChange(val);
        }
    }

    const handleInputChange = (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        // //console.log("Changed", e.target.value, e.target.name);
        setInputValue(e.target.value);
        if (moment(e.target.value).isValid()) {
            //console.log(moment(e.target.value));
            setDatePickerValue(moment(e.target.value));
            onChange && onChange(moment(e.target.value), props.name);
        }
    }

    const selectToday = (e: React.SyntheticEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (onConfirm) {
            setDatePickerValue(moment());
        } else {
            onChange && onChange(moment())
        }
    }

    const handleCancel = (e: React.SyntheticEvent | null = null) => {
        e && e.preventDefault();
        e && e.stopPropagation();
        setOpen(false);
        if (onConfirm) {
            setDatePickerValue(moment(value));
        }
    }

    const handleConfirm = (e: React.SyntheticEvent) => {
        e.preventDefault();
        e.stopPropagation();
        let keepOpen = false;
        if (props.name) {
            const returnValue: any = onChange(datePickerValue, props.name);
            if(returnValue === true) keepOpen = true;
        } else {
            const returnValue: any = onChange(datePickerValue);
            if(returnValue === true) keepOpen = true;
        }
        setOpen(props.useCloseCallback ? keepOpen : false);
    }

    const handle15MinChange = (e: React.SyntheticEvent, direction: "next" | "prev") => {
        e.preventDefault();
        e.stopPropagation();
        // //console.log("Starting date: ", datePickerValue.valueOf(), new Date(datePickerValue.valueOf()));
        let newDate = datePickerValue.valueOf();
        let fifteenMin = 1000 * 60 * 15;
        if (direction === "next") {
            newDate += 1;
            newDate = Math.ceil(newDate / fifteenMin) * fifteenMin;
        } else {
            newDate -= 1;
            newDate = Math.floor(newDate / fifteenMin) * fifteenMin;
        }
        setDatePickerValue(moment(newDate));
        // onChange&&onChange(moment(newDate), props.name);
    }

    React.useEffect(() => {
        //console.log("Formatting Date: ", inputTimeFormat, value, moment(value).format(inputTimeFormat));
        if(isUTC){
            setInputValue(moment.utc(value).format(inputTimeFormat));
        }else{
            setInputValue(moment(value).format(inputTimeFormat));
        }
        setDatePickerValue(moment(value));
        // eslint-disable-next-line 
    }, [value])

    return (
        <>
            {
                type === "input"
                    ?
                    <InputGroup className={props.inputWrapperClassName}>
                        <Form.Control
                            type={
                                isFirefox
                                ?"text"
                                :
                                (showTime && !showDate)
                                ? "time"
                                : showTime
                                    ? "datetime-local"
                                    : "date"}
                            onChange={handleInputChange}
                            value={(typeof inputValue === "string" && inputValue.toUpperCase().includes("INVALID")) ? errorMessage : inputValue}
                            // id="DatePicker-input"
                            className="DatePicker-input"
                            readOnly={isFirefox}
                            onClick={(e: React.SyntheticEvent) => { e.preventDefault(); e.stopPropagation(); }}
                            style={{ borderRight: "none" }}
                            {...props.inputProps}
                            // disabled={props.disabled}
                            disabled={true} //cgm 04152022 =>> temporary fix to keep date picker from crashing the app
                            id={inputId}
                        />
                        <InputGroup.Append id="date-picker-icon" className={`border border-left-0 ${appendClassName}`} style={{ width: 30, zIndex: 4, borderColor: "rgb(128, 189, 255)" }} onClick={handleClick}>
                            <i className="material-icons d-flex align-items-center cursor-on-hover action-icon">today</i>
                        </InputGroup.Append>
                    </InputGroup>
                    : type === "button"
                        ? <IconButton icon="calendar_today" variant="outline-secondary" onClick={handleClick} {...props.buttonProps}>{moment(value).isValid() ? moment(value).format(showTime ? "MM/DD/YYYY hh:mm a" : "YYYY-MM-DD") : "Select Date"}</IconButton>
                        : type === "effective-date-icon"
                            ?
                            <div className="py-0 px-1 h-auto">
                                <ReactTooltip
                                    effect="solid"
                                    place="right"
                                    id={`DatePicker-Tooltip-${ttId}-${props.name}`}
                                >
                                    <span>{hasSelected ? `Effective Date : ${moment(value).format('LL')}` : `Select Effective Date`}</span>
                                </ReactTooltip>
                                <div onClick={() => setOpen(true)} className="border-0 m-0 p-0 cursor-on-hover action-icon">
                                    {
                                        hasSelected
                                            ? <i data-tip="" data-for={`DatePicker-Tooltip-${ttId}-${props.name}`} className={`mr-1 effective-icon far fa-calendar-check text-success`}></i>
                                            : <i data-tip="" data-for={`DatePicker-Tooltip-${ttId}-${props.name}`} className={`effective-icon far fa-calendar`}></i>
                                    }
                                </div>
                            </div>
                            : <CustomInput openDatePicker={() => setOpen(true)} value={moment(value).format(showTime ? "MM/DD/YYYY hh:mm a" : "YYYY-MM-DD")} />
            }
            {!props.disabled && <Modal id="DatePicker-Modal" className="z-100" onHide={handleCancel} show={open} centered onClick={(e: React.SyntheticEvent) => { e.preventDefault(); e.stopPropagation(); }}>
                <div className="" style={{ width: 400, height: 420 }}>
                    <span onClick={selectToday} className="btn btn-link" style={{ position: "absolute", zIndex: 2, marginTop: -30, backgroundColor: "#FFF" }}>Set to Today {showTime && "and now"}</span>
                    <div id="DatePicker" style={{ height: 397, width: "100%" }}>
                        <Datetime
                            value={onConfirm ? datePickerValue : moment(props.value)}
                            open={true}
                            input={false}
                            timeFormat={showTime}
                            dateFormat={showDate}
                            utc={isUTC}
                            // renderInput={() => null}
                            renderView={(mode: string, renderDefault: any) => {
                                if (mode === "time" && props.showAdditionalTimeControls) {
                                    return <div className="d-flex flex-row justify-content-center align-items-center">
                                        {renderDefault()}
                                        <div className="d-flex flex-column">
                                            <span title="Forwards 15 minutes" onClick={(e: any) => handle15MinChange(e, "next")} className="d-flex justify-content-center align-items-center cursor-on-hover action-icon">
                                                <i className="material-icons">update</i>15
                                            </span>
                                            <span title="Backwards 15 minutes" onClick={(e: any) => handle15MinChange(e, "prev")} className="d-flex justify-content-center align-items-center cursor-on-hover action-icon">
                                                <i className="material-icons">restore</i>15
                                            </span>
                                        </div>
                                    </div>
                                }
                                return renderDefault();
                            }}
                            onChange={handleChange}
                        />
                    </div>
                </div>
                {
                    onConfirm &&
                    <div className="d-flex flex-row">
                        <IconButton onClick={handleCancel} icon="close">Cancel</IconButton>
                        <IconButton onClick={handleConfirm} icon="event_available">Confirm</IconButton>
                    </div>
                }
            </Modal>}
        </>
    )
}

DatePicker.defaultProps = {
    CustomInput: null,
    type: "input",
    showTime: false,
    onChange: () => null,
    name: "",
    hasSelected: false,
    formatBack: null,
    onConfirm: true,
    inputWrapperClassName: "",
    showAdditionalTimeControls: false,
    useCloseCallback: false,
    appendClassName: "",
    showDate: true,
    isUTC: false,
    inputId: "",
    errorMessage: "Choose Date"
}

export default DatePicker;